import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import axios, { CancelTokenSource } from 'axios'
import cn from 'classnames'
import { Popup } from '../../../../components/Popup'
import { Table } from '../../../../components/Table'
import { Input } from '../../../../components/Input'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { useDebounce } from '../../../../hooks/hooks'
import { getHSList, addHSToCC } from './api'
import { useContractCategoryDetailsContext } from '../../Providers/ContractCategoryDetailsContextProvider'
import { useContractCategoryDetailsPopup } from '../../Providers/ContractCategoryDetailsPopupProvider'
import { setLoading } from '../../../../redux/store/common/slice'
import { DEFAULT_PAGE } from '../../../../components/Table/constants'
import { BTN_TXT, PLACEHOLDERS } from '../../../../constants'
import { columns } from './constants'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import {
  AddHealthSystemParams,
  AddHealthSystemPopupProps,
  AddHealthSystemRequestData
} from './types'
import './styles.scss'
import './../styles.scss'
import { ReactComponent as ArrowDown16 } from '../../../../assets/svg/ArrowDown16.svg'
import { ReactComponent as ArrowRight16 } from '../../../../assets/svg/ArrowRight16.svg'
import { LED_BY_VALUE } from '../../../../components/LeadByIndicator/constants'
import { ICCHealthSystem, ICCHealthSystemWithHospitals } from '../types'

export const AddHealthSystemPopup = ({
  updateList,
  canSeeHealthSystem
}: AddHealthSystemPopupProps) => {
  const [totalNumber, setTotalNumber] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [firstLoad, setFirstLoad] = useState(true)
  const [sortParams, setSortParams] = useState<AddHealthSystemRequestData>()
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [tableData, setTableData] = useState<ICCHealthSystemWithHospitals[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<ICCHealthSystem[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const { details } = useContractCategoryDetailsContext().state
  const { addHealthSystemPopup } = useContractCategoryDetailsPopup()

  const dispatch = useDispatch()

  const debouncedSearch = useDebounce(searchValue, 500)

  const hideSelectHSLevel = useMemo(() => {
    return [
      LED_BY_VALUE.HEALTH_SYSTEM,
      LED_BY_VALUE.LIFELINE_FOR_HEALTH_SYSTEM,
      LED_BY_VALUE.NATIONAL_GPO
    ].includes(details.led_by)
  }, [details.led_by])

  const displayData = useMemo(
    () =>
      canSeeHealthSystem
        ? tableData
        : (tableData.flatMap(
            (hs) => hs.children
          ) as ICCHealthSystemWithHospitals[]),
    [canSeeHealthSystem, tableData]
  )

  const tableColumns = useMemo(
    () => columns(canSeeHealthSystem),
    [canSeeHealthSystem]
  )

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (addHealthSystemPopup.state.visible) {
      timeoutRef.current = setTimeout(
        () => {
          getTableData({
            ...sortParams,
            page: { ...DEFAULT_PAGE }
          })
          setPageInfo({ ...DEFAULT_PAGE })
        },
        firstLoad ? 0 : 500
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, addHealthSystemPopup.state.visible])

  const getTableData = async ({
    sortField,
    sortOrder,
    page
  }: AddHealthSystemRequestData) => {
    try {
      dispatch(setLoading(true))
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
      const params: AddHealthSystemParams = {}
      if (searchValue) {
        params.search = searchValue
      }
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ? page : pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      tableDataTokenRef.current = axios.CancelToken.source()
      const { data } = await getHSList(details.uuid, {
        cancelToken: tableDataTokenRef.current?.token,
        params
      })
      setTableData(
        data.results.map((i) => ({
          ...i,
          key: i.uuid,
          hospital_level: false,
          children: i.hospitals.map((hospital) => {
            return {
              ...hospital,
              key: hospital.uuid,
              hospital_level: true
            }
          })
        }))
      )
      setTotalNumber(data.count)
      if (firstLoad) {
        setFirstLoad(false)
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const onAddHSToCC = async () => {
    try {
      dispatch(setLoading(true))
      await addHSToCC(
        details.uuid,
        {
          hospitals: selectedRows
            .filter((selectedRow) => selectedRow.hospital_level)
            .map((selectedRow) => selectedRow.uuid)
        },
        `${selectedRows.length > 1 ? 'Facilities' : 'Facility'} ${
          selectedRows.length > 1 ? 'have' : 'has'
        } been added to the category.`
      )
      onCancel()
      updateList()
    } catch (e) {
      dispatch(setLoading(false))
    }
  }

  const onCancel = () => {
    addHealthSystemPopup.actions.close()
    handleSelectRow([], [])
    setExpandedRowKeys([])
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const handleSelectRow = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRows)
  }

  return (
    <Popup
      visible={addHealthSystemPopup.state.visible}
      onCancel={onCancel}
      width="100%"
      title={canSeeHealthSystem ? 'Add Health System' : 'Add Hospital'}
      className="add-health-system-popup"
      footer={
        <>
          <Button upperCase onClick={onCancel}>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            disabled={!selectedRowKeys.length}
            onClick={onAddHSToCC}
            upperCase
          >
            {BTN_TXT.ADD}
          </Button>
        </>
      }
    >
      {!selectedRowKeys.length && (
        <Input
          className="add-health-system-popup__search allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            placeholder: PLACEHOLDERS.SEARCH,
            prefix: <Search16 />,
            value: searchValue,
            onChange: (e) => setSearchValue(e.target.value)
          }}
        />
      )}
      <Table
        className={cn('mt-4 contacts-hs-list-table', {
          selectable: canSeeHealthSystem
        })}
        dataSource={displayData}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        columns={tableColumns}
        onChangePage={handleChangePageSize}
        onChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: handleSelectRow,
          hideSelectAll: hideSelectHSLevel,
          checkStrictly: false,
          getCheckboxProps: ({ hospital_level }) => {
            return {
              disabled: hideSelectHSLevel ? !hospital_level : false
            }
          }
        }}
        expandable={
          canSeeHealthSystem
            ? {
                defaultExpandAllRows: false,
                columnWidth: 40,
                expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) setExpandedRowKeys([record.uuid])
                  else setExpandedRowKeys([])
                },
                expandIcon: ({ expanded, onExpand, record, expandable }) =>
                  expandable ? (
                    expanded ? (
                      <div
                        className="expand-wrapper row gap-12 align-center"
                        onClick={(e: React.MouseEvent<HTMLElement>) =>
                          onExpand(record, e)
                        }
                      >
                        <ArrowDown16 />
                      </div>
                    ) : (
                      <div
                        className="expand-wrapper row gap-12 align-center"
                        onClick={(e: React.MouseEvent<HTMLElement>) =>
                          onExpand(record, e)
                        }
                      >
                        <ArrowRight16 />
                      </div>
                    )
                  ) : (
                    <></>
                  )
              }
            : undefined
        }
      />
    </Popup>
  )
}
